import React, { useState, useEffect } from "react";
import AuthLayout from "../components/authLayout";
import { Form } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import BrandLogo from "assets/images/svg/click-for-marry02.svg";
import Input from "components/BaseWidgets/form/input";
import Password from "components/BaseWidgets/form/password";
import Button from "components/BaseWidgets/button";
import OtpInput from "components/BaseWidgets/form/otp";
import RadioButton from "components/BaseWidgets/radioButton";
import Select from "components/BaseWidgets/select";
import { useMutation } from "@tanstack/react-query";
import {
  requestForgotPasswordOtp,
  verifyForgotPasswordOtp,
  changePassword,
} from "api/auth";
import AlertNotification from "components/CustomWidgets/alertNotification";
import SuccessModal from "components/Account/components/successModal/index ";
import { clearStorage } from "utils/storage";
import { useSelectCode } from "hooks/api/useListing";

const ForgotPassword = () => {
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [processedIdentifier, setProcessedIdentifier] = useState("");
  const [loginMethod, setLoginMethod] = useState("phone");
  const [countryCode, setCountryCode] = useState(null);
  const [otpValue, setOtpValue] = useState("");
  const [counter, setCounter] = useState(120);
  const [resetToken, setResetToken] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: countrylist } = useSelectCode({ search_key: "" });
  const countryCodesOptions = countrylist?.country_codes?.map((item) => ({
    value: item.id,
    label: item.code,
    phone_length: item.phone_length,
  }));

  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === "#reset") {
        setStep(2);
      } else {
        setStep(1);
      }
    };

    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [identifier, countryCode]);

  useEffect(() => {
    if (location.state && countrylist?.country_codes) {
      const { identifier, countryCode: stateCountryCode } = location.state;

      if (identifier && identifier !== processedIdentifier) {
        setIdentifier(identifier);
        setProcessedIdentifier(identifier);
        form.setFieldsValue({ identifier });
      }

      if (stateCountryCode && !countryCode) {
        const selectedCountry = countrylist.country_codes.find(
          (c) => c.id === stateCountryCode,
        );

        if (selectedCountry) {
          setCountryCode({
            value: selectedCountry.id,
            label: selectedCountry.code,
            phone_length: selectedCountry.phone_length,
          });
        }
      }

      const timer = setTimeout(() => {
        const payload = {
          identifier: identifier,
          countrycode: countryCode?.value,
        };

        if (payload) {
          sendOtpMutation.mutate(payload);
        }
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [location.state, countrylist?.country_codes, countryCode]);

  useEffect(() => {
    if (
      countryCodesOptions &&
      !countryCode &&
      window.location.hash !== "#reset"
    ) {
      const defaultCode = countryCodesOptions.find((c) => c.label === "+91");
      if (defaultCode) setCountryCode(defaultCode);
    }
  }, [countryCodesOptions, countryCode]);

  useEffect(() => {
    clearStorage();
  }, []);

  // ✅ Send OTP
  const sendOtpMutation = useMutation({
    mutationFn: requestForgotPasswordOtp,
    onSuccess: (data) => {
      if (data.success) {
        AlertNotification("success", "OTP sent successfully");
        setStep(2);
        setCounter(120);
      }
    },
  });

  const verifyOtpMutation = useMutation({
    mutationFn: verifyForgotPasswordOtp,
    onSuccess: (data) => {
      if (data?.success && data?.data?.token) {
        AlertNotification("success", "OTP verified successfully");
        setResetToken(data.data.token);
        setStep(3);
      }
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: changePassword,
    onSuccess: (data) => {
      if (data.success) {
        setOpenModal(true);
      }
    },
  });

  const handleSendOtp = () => {
    form
      .validateFields()
      .then((values) => {
        let payload = {
          identifier: values.identifier,
        };

        if (loginMethod === "phone" && countryCode) {
          let phoneNumber = values.identifier.replace(/\D/g, "");
          const codeWithoutPlus = countryCode.label.replace("+", "");
          if (phoneNumber.startsWith(codeWithoutPlus)) {
            phoneNumber = phoneNumber.substring(codeWithoutPlus.length);
          }

          payload.identifier = phoneNumber;
          payload.countrycode = countryCode.value;
          setProcessedIdentifier(phoneNumber);
        } else {
          setProcessedIdentifier(values.identifier);
        }

        if (!payload.countrycode) {
          delete payload.countrycode;
        }

        sendOtpMutation.mutate(payload);
      })
      .catch((err) => {
      });
  };

  const handleVerifyOtp = () => {
    if (otpValue.length !== 6) return;

    const payload = {
      identifier: processedIdentifier,
      otp: otpValue,
      ...(loginMethod === "phone" && countryCode?.value
        ? { countrycode: countryCode.value }
        : {}),
    };

    verifyOtpMutation.mutate(payload);
  };

  const handleChangePassword = (values) => {
    if (!resetToken) {
      AlertNotification("error", "Invalid session. Please try again.");
      setStep(1);
      return;
    }
    if (values.password !== values.confirmPassword) {
      AlertNotification("error", "Passwords do not match");
      return;
    }
    changePasswordMutation.mutate({
      new_password: values.password,
      confirm_password: values.confirmPassword,
      token: resetToken,
    });
  };

  const handleResend = () => {
    if (!processedIdentifier) {
      AlertNotification("error", "Missing identifier. Please try again.");
      setStep(1);
      return;
    }

    const payload = {
      identifier: processedIdentifier,
      ...(loginMethod === "phone" && countryCode?.value
        ? { countrycode: Number(countryCode.value) }
        : {}),
    };

    sendOtpMutation.mutate(payload);
  };

  const formatTime = () => {
    const m = Math.floor(counter / 60);
    const s = counter % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  useEffect(() => {
    if (step === 2 && counter > 0) {
      const timer = setTimeout(() => setCounter(counter - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [counter, step]);

  const getInputPlaceholder = () => {
    switch (loginMethod) {
      case "phone":
        return "Enter phone number";
      case "email":
        return "Enter email address";
      case "aec":
        return "Enter AEC ID";
      default:
        return "";
    }
  };

  const getInputLabel = () => {
    switch (loginMethod) {
      case "phone":
        return "Phone Number";
      case "email":
        return "Email ID";
      case "aec":
        return "AEC ID";
      default:
        return "Identifier";
    }
  };

  const getValidationRules = () => {
    const baseRules = [
      { required: true, message: `Please enter your ${loginMethod}` },
    ];

    if (loginMethod === "phone") {
      baseRules.push({
        pattern: /^[0-9]+$/,
        message: "Please enter a valid phone number (numbers only)",
      });
      if (countryCode?.phone_length) {
        baseRules.push({
          validator: (_, value) => {
            if (value && value.length !== countryCode.phone_length) {
              return Promise.reject(
                new Error(
                  `Phone number should be ${countryCode.phone_length} digits`,
                ),
              );
            }
            return Promise.resolve();
          },
        });
      }
    } else if (loginMethod === "email") {
      baseRules.push({
        type: "email",
        message: "Please enter a valid email address",
      });
    } else if (loginMethod === "aec") {
      baseRules.push({
        pattern: /^[a-zA-Z0-9]*$/,
        message: "AEC ID should not contain special characters",
      });
    }

    return baseRules;
  };

  return (
    <AuthLayout childrenClass="custom-auth-layout">
      <div className="login-otp-wrapper">
        <div className="brand-logo">
          <img className="logo" src={BrandLogo} alt="Logo" />
        </div>

        {/* STEP 1: SEND OTP */}
        {step === 1 && (
          <>
            <div className="page-heading">
              <h2 className="title font-title-xl mb-2">Forgot password?</h2>
              <p className="subtitle font-body-xs">
                Enter your mobile number, email ID, or AEC ID to receive an OTP.
              </p>
            </div>
            <Form form={form} layout="vertical">
              <Form.Item className="mb-4" style={{ marginBottom: 0 }}>
                <div className="login-bundle-box">
                  <div className="login-method-selector">
                    <RadioButton
                      value={loginMethod}
                      className="radio-button-label"
                      radioGroupClass="radio-group"
                      options={[
                        { label: "Phone number", value: "phone" },
                        { label: "Email ID", value: "email" },
                        { label: "AEC ID", value: "aec" },
                      ]}
                      onChange={(e) => {
                        setLoginMethod(e.target.value);
                        setIdentifier("");
                        setProcessedIdentifier("");
                        form.setFieldsValue({ identifier: "" });
                      }}
                    />
                  </div>
                  <div className="login-fields-group">
                    {loginMethod === "phone" && (
                      <div className="country-code-select">
                        <Select
                          // showSearch={false}
                          value={countryCode}
                          options={countryCodesOptions}
                          inputClassName={"user-onboarding-select"}
                          onChange={(value) => {
                            const selected = countryCodesOptions.find(
                              (item) => item.value === value,
                            );
                            setCountryCode(selected);
                            setIdentifier("");
                            setProcessedIdentifier("");
                            form.setFieldsValue({ identifier: "" });
                          }}
                          allowClear={false}
                        />
                      </div>
                    )}
                    <div
                      className={`custom-error input-field ${loginMethod === "phone" ? "phone-input-padding" : ""
                        }`}
                      style={{ marginBottom: 0 }}
                    >
                      <Form.Item
                        name="identifier"
                        noStyle
                        rules={getValidationRules()}
                      >
                        <Input
                          id="identifier"
                          label={getInputLabel()}
                          placeholder={getInputPlaceholder()}
                          value={identifier}
                          maxLength={
                            loginMethod === "phone"
                              ? countryCode?.phone_length
                              : undefined
                          }
                          onChange={(e) => {
                            let value = e.target.value;

                            if (loginMethod === "phone") {
                              value = value.replace(/\D/g, "");
                            } else if (loginMethod === "aec") {
                              value = value.replace(/[^a-zA-Z0-9]/g, "");
                            }

                            setIdentifier(value);
                            form.setFieldsValue({ identifier: value });
                          }}
                        />
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </Form.Item>
              <Button
                loading={sendOtpMutation.isLoading}
                onClick={handleSendOtp}
                wFull
                disabled={!identifier.trim()}
                style={{ marginTop: 20 }}
              >
                Send OTP
              </Button>
            </Form>
          </>
        )}

        {/* STEP 2: VERIFY OTP */}
        {step === 2 && (
          <>
            <div className="page-heading">
              <h2 className="title font-title-xl mb-2">OTP verification</h2>
              <div className="otp-info-line">
                <p className="subtitle user-identifier font-body-xs">
                  Enter OTP sent to{" "}
                  <span className="identifier">
                    {loginMethod === "phone"
                      ? `${countryCode ? countryCode?.label : ""}${processedIdentifier}`
                      : processedIdentifier}
                  </span>
                </p>
                {!window.location.hash && (
                  <Button
                    className="change-link"
                    variant="link"
                    onClick={() => setStep(1)}
                  >
                    Change
                  </Button>
                )}
              </div>
            </div>

            <Form
              className="login-form"
              layout="vertical"
              onFinish={handleVerifyOtp}
            >
              <Form.Item
                name="otp"
                validateTrigger="onSubmit"
                className="custom-error"
                rules={[
                  {
                    validator: () =>
                      otpValue.length === 6
                        ? Promise.resolve()
                        : Promise.reject(new Error("Please enter 6-digit OTP")),
                  },
                ]}
              >
                <OtpInput
                  id="otp"
                  mask="•"
                  placeholder="------"
                  value={otpValue}
                  setValue={setOtpValue}
                />
              </Form.Item>
              <p className="otp-timer mt-4 text-sm text-primary">
                {counter > 0 ? (
                  <>
                    Resend OTP in: <span className="timer">{formatTime()}</span>
                  </>
                ) : (
                  <Button
                    className="change-link"
                    variant="link"
                    onClick={handleResend}
                    isLoading={sendOtpMutation.isLoading}
                  >
                    Resend
                  </Button>
                )}
              </p>
              <Button
                htmlType="submit"
                isLoading={verifyOtpMutation.isLoading}
                className="mt-8 mb-6"
                wFull
                disabled={otpValue.length !== 6}
              >
                Submit
              </Button>
            </Form>
          </>
        )}

        {/* STEP 3: CHANGE PASSWORD */}
        {step === 3 && (
          <>
            <div className="page-heading">
              <h2 className="title font-title-xl mb-2">Reset password</h2>
              <p className="subtitle font-body-xs">
                Set a new password that's different from the previous password.
              </p>
            </div>
            <Form form={form} layout="vertical" onFinish={handleChangePassword}>
              <Form.Item
                name="password"
                rules={[
                  { required: true, message: "Please enter a new password" },
                  {
                    validator: (_, value) => {
                      if (value) {
                        const strongPasswordRegex =
                          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
                        if (!strongPasswordRegex.test(value)) {
                          return Promise.reject(
                            "Password must be at least 8 characters long, include uppercase, lowercase, number, and special character!",
                          );
                        }
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Password label="Password" />
              </Form.Item>
              <Form.Item
                name="confirmPassword"
                dependencies={["password"]}
                rules={[
                  { required: true, message: "Please confirm your password" },
                  {
                    validator: (_, value) => {
                      if (value) {
                        const strongPasswordRegex =
                          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
                        if (!strongPasswordRegex.test(value)) {
                          return Promise.reject(
                            "Password must be at least 8 characters long, include uppercase, lowercase, number, and special character!",
                          );
                        }
                      }
                      return Promise.resolve();
                    },
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Passwords do not match"),
                      );
                    },
                  }),
                ]}
              >
                <Password label="Confirm Password" />
              </Form.Item>
              <Button
                isLoading={changePasswordMutation.isPending}
                htmlType="submit"
                wFull
              >
                Change Password
              </Button>
            </Form>
          </>
        )}

        <SuccessModal
          open={openModal}
          onOk={() => {
            setOpenModal(false);
            navigate("/login");
          }}
          title="Password Changed!"
          description="Your password has been changed successfully. Use your new password to log in."
        />
      </div>
    </AuthLayout>
  );
};

export default ForgotPassword;
